import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/** Ответ «Да» на вопрос «А ты точно родитель?» живёт ровно до конца сеанса
 *  ЭТОГО родителя.
 *
 *  Не дольше: планшет остаётся у ребёнка, а в той же загрузке страницы может
 *  войти другой взрослый. Не короче: внутри сеанса родитель ходит по разделу
 *  туда-сюда, и вопрос на каждом шаге приучил бы жать «Да» не глядя.
 *
 *  Время жизни задаёт МЕСТО поставщика, а не код внутри него. Он стоит в
 *  `Shell` (`src/App.tsx`) внутри каждой из двух ветвей шлюза входа — без
 *  учётной записи и под заслоном владельца. Выход любым путём — «Выйти» в
 *  разделе (со стиранием), «Выйти» в заслоне владельца (без стирания),
 *  `SIGNED_OUT` при неудачном обновлении токена — снимает ветвь: между
 *  выходом и новым входом шлюз входа приложение не отрисовывает вовсе.
 *  Вход того же или другого родителя монтирует ветвь заново, и ответ
 *  рождается пустым. На ключ заслона владельца полагаться нельзя: у ТОГО
 *  ЖЕ родителя он не меняется.
 *
 *  Прежде ответ жил в модульной переменной: его не сбрасывал ни один выход,
 *  и родитель, вошедший после другого, попадал в раздел без вопроса; в
 *  прогоне тестов ответ протекал между тестами одного потока. Поставщик НАД
 *  шлюзом входа повторил бы ту же дыру — он переживает выход. Обе поломки
 *  ловят проверки `tests/ui/App.test.tsx` (карточка 6 плана
 *  `docs/superpowers/plans/2026-09-24-reliability-and-debt.md`).
 *
 *  Защитой вопрос не является и не назывался — граница названа в
 *  `ParentGate`. Это согласованность: обе двери раздела спрашивают
 *  одинаково и одинаково долго помнят ответ. */
export interface ParentAnswer {
  ответил: boolean;
  ответить: () => void;
}

const ParentAnswerContext = createContext<ParentAnswer | null>(null);

export function ParentAnswerProvider({ children }: { children: ReactNode }) {
  const [answered, setAnswered] = useState(false);
  const answer = useCallback(() => setAnswered(true), []);
  const value = useMemo(() => ({ ответил: answered, ответить: answer }), [answered, answer]);
  return <ParentAnswerContext.Provider value={value}>{children}</ParentAnswerContext.Provider>;
}

export function useParentAnswer(): ParentAnswer {
  const value = useContext(ParentAnswerContext);
  /** Отказ, а не «не отвечал». Дверь вне поставщика спрашивала бы вечно —
   *  или, при подстановке «ответил», не спрашивала бы никогда; ни то ни
   *  другое не видно, пока не нажмёшь. */
  if (!value) throw new Error('родительский раздел вне ParentAnswerProvider');
  return value;
}
